'use client';

import { useMemo } from 'react';

import {
  FEATURES,
  getFeatureDefinition,
  type BooleanFeatureName,
  type FeatureName,
  type FeatureValue,
} from '@/feature-flags/consts/features.const';
import type { FlagState } from '@/feature-flags/providers/FeatureFlagProvider';
import { useFeatureFlagContext, useFlagState } from '@/feature-flags/providers/FeatureFlagProvider';

export interface UseFeatureFlagResult<TValue> {
  value: TValue;
  loading: boolean;
  error: Error | null;
  isFallback: boolean;
  variant: string | null;
}

function readValue<TName extends FeatureName>(name: TName, state: FlagState): FeatureValue<TName> {
  const { fallback } = getFeatureDefinition(name);

  if (!state.match || state.error) {
    return fallback;
  }

  return (state.values[name] ?? fallback) as FeatureValue<TName>;
}

export function useFeatureFlag<TName extends FeatureName>(
  name: TName,
): UseFeatureFlagResult<FeatureValue<TName>> {
  const state = useFlagState(getFeatureDefinition(name).flagKey);

  return {
    value: readValue(name, state),
    loading: state.loading,
    error: state.error,
    isFallback: state.isFallback,
    variant: state.variant,
  };
}

export interface UseEnabledFeaturesResult<TName extends BooleanFeatureName> {
  enabled: TName[];
  loading: boolean;
}

export function useEnabledFeatures<TName extends BooleanFeatureName>(
  names: readonly TName[],
): UseEnabledFeaturesResult<TName> {
  const { flags } = useFeatureFlagContext();

  return useMemo(() => {
    const enabled = names.filter((name) => {
      const state = flags[FEATURES[name].flagKey];

      return Boolean(state && readValue(name, state) === true);
    });

    const loading = names.some((name) => flags[FEATURES[name].flagKey]?.loading ?? true);

    return { enabled, loading };
  }, [names, flags]);
}
